import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import "./BoardContextMenu.scss";

export type SidebarMenuItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
};

type Props = {
  open: boolean;
  x: number;
  y: number;
  items: SidebarMenuItem[];
  onClose: () => void;
  onSelect: (id: string) => void;
};

export const SidebarContextMenu = ({
  open,
  x,
  y,
  items,
  onClose,
  onSelect,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });

  useLayoutEffect(() => {
    if (!open || !ref.current) {
      return;
    }
    const rect = ref.current.getBoundingClientRect();
    const pad = 8;
    let nextX = x;
    let nextY = y;
    if (nextX + rect.width > window.innerWidth - pad) {
      nextX = Math.max(pad, window.innerWidth - rect.width - pad);
    }
    if (nextY + rect.height > window.innerHeight - pad) {
      nextY = Math.max(pad, window.innerHeight - rect.height - pad);
    }
    setPos({ x: nextX, y: nextY });
  }, [open, x, y, items]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const onScroll = () => onClose();
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      ref={ref}
      className="board-ctx"
      style={{ left: pos.x, top: pos.y }}
      role="menu"
    >
      <div className="board-ctx__group">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            className={`board-ctx__item${
              item.danger ? " board-ctx__item--danger" : ""
            }`}
            onClick={() => {
              onSelect(item.id);
              onClose();
            }}
          >
            {item.icon ? (
              <span className="board-ctx__icon">{item.icon}</span>
            ) : null}
            <span className="board-ctx__label">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
